import React, { useContext, useEffect, useState } from 'react';
import { Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import {
  ControlContainer,
  PageContent,
  PageHeader,
  PageSection,
  StyledCard,
  Subtitle,
} from 'common/styles/page';
import { useApiPortalV1AssessmentsResultsListQuery } from 'areas/assessment-schedule/api';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { getSelectedAssessmentPeriodId } from 'common/selectors/auth';
import { useApiPortalV1EmployeeRecordsByPeriodAndValueRetrieveQuery } from 'store/generated/portal/employee-records';
import { usePageCopilotPrompts } from 'areas/copilot/components/CopilotPromptsContext';

import { getCurrentOrganizationId } from '../../../common/selectors/auth';
import { TopBarContext } from 'common/layouts/AuthLayout/TopBarContext';
import TopBarControls from 'common/layouts/AuthLayout/TopBarAssessmentPeriod';
import { StyledIconButton } from 'common/components/button/StyledButton';
import IndividualRecordDataModal from './OutcomesIndividualRecordModal';
import OutcomesIndividualDataTable from './OutcomesIndividualDataTable';
import { Confirm } from 'semantic-ui-react';
import { employeeRecApi } from 'areas/settings/employee-management/api/employeeRecordApi';
import { EmployeeRecordRead } from 'store/generated/portal/employee-records';

import { AggregateType } from 'common/constants/AggregateType';
ChartJS.register(LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

type SelectOptions = { value: number; label: string };

const OutcomesIndividual = () => {
  const currentAssessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const { data: employee_records } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.MEMBER_PILLARS,
    assessmentPeriodId: currentAssessmentPeriodId,
  });
  const { data: data_by_option } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.CATEGORY_PILLARS,
    assessmentPeriodId: currentAssessmentPeriodId,
  });
  const { data: lookups } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.LOOKUP_PROPERTIES,
    assessmentPeriodId: currentAssessmentPeriodId,
  });

  const [selectedRecord, setSelectedRecord] = useState<number>(0);
  const [selectedRecordName, setSelectedRecordName] = useState<string | null>(null);

  // Fetch employee record values for the selected record type and assessment period
  const { data: employee_record_values, isLoading: isLoadingRecordValues } = useApiPortalV1EmployeeRecordsByPeriodAndValueRetrieveQuery(
    {
      organizationId: useSelector(getCurrentOrganizationId),
      assessmentPeriodId: currentAssessmentPeriodId,
      employeeRecordValueId: selectedRecord,
    },
    {
      skip: !currentAssessmentPeriodId || selectedRecord === 0,
    }
  );

  const [filterPillars, setFilterPillars] = useState<SelectOptions[]>([]);
  const [filterRecords, setFilterRecords] = useState<SelectOptions[]>([]);

  const [selectedPillar, setSelectedPillar] = useState<number>(0);
  const [selectedPillarName, setSelectedPillarName] = useState<string | null>(null);

  const [chartData, setChartData] = useState([]);
  const [chartTrendLine, setChartTrendLine] = useState([{ x: 0, y: 0 }]);
  const [showDataTable, setShowDataTable] = useState<boolean>(false);
  const { setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro } = usePageCopilotPrompts();

  // Modal and delete state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<EmployeeRecordRead | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | undefined>(undefined);
  const [selectedEmployeeName, setSelectedEmployeeName] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<EmployeeRecordRead | null>(null);

  const [deleteRecord] = employeeRecApi.useApiPortalV1EmployeeRecordsDestroyMutation();

  const organizationId = useSelector(getCurrentOrganizationId);
  const { setTopBarContent } = useContext(TopBarContext);

  const handleAddRecord = (employeeId: number, employeeName: string) => {
    setEditingRecord(null);
    setSelectedEmployeeId(employeeId);
    setSelectedEmployeeName(employeeName);
    setIsModalOpen(true);
  };

  const handleEditRecord = (record: any, employeeName: string) => {
    // Convert the record from the new API format to EmployeeRecordRead format
    const employeeRecord: EmployeeRecordRead = {
      id: record.record_id,
      employee: record.employee_id,
      employee_details: '',
      value_name: selectedRecordName || '',
      employee_record_value: {
        id: selectedRecord,
        name: selectedRecordName || '',
        type_of_value: 2,
        organization: organizationId.toString(),
      },
      employee_record_value_id: selectedRecord,
      assessment_period: currentAssessmentPeriodId.toString(),
      value: record.value,
      created_at: record.created_at,
      updated_at: record.updated_at,
    };
    setEditingRecord(employeeRecord);
    setSelectedEmployeeId(record.employee_id);
    setSelectedEmployeeName(employeeName);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (record: any) => {
    // Convert the record from the new API format to EmployeeRecordRead format
    const employeeRecord: EmployeeRecordRead = {
      id: record.record_id,
      employee: record.employee_id,
      employee_details: '',
      value_name: selectedRecordName || '',
      employee_record_value: {
        id: selectedRecord,
        name: selectedRecordName || '',
        type_of_value: 2,
        organization: organizationId.toString(),
      },
      employee_record_value_id: selectedRecord,
      assessment_period: currentAssessmentPeriodId.toString(),
      value: record.value,
      created_at: record.created_at,
      updated_at: record.updated_at,
    };
    setRecordToDelete(employeeRecord);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (recordToDelete) {
      try {
        await deleteRecord({ id: recordToDelete.id }).unwrap();
        setShowDeleteConfirm(false);
        setRecordToDelete(null);
      } catch (error) {
        console.error('Failed to delete employee record:', error);
      }
    }
  };


  useEffect(() => {
    setTopBarContent(<TopBarControls />);
    return () => setTopBarContent(null);
  }, [setTopBarContent, organizationId]);

  useEffect(() => {
    const recordLabel = selectedRecordName || 'the selected employee record';
    const pillarLabel = selectedPillarName || 'the selected pillar';

    setPagePrompts([
      {
        title: 'Individual Outcomes Insights',
        displayText: `Summarize what individual outcome data is showing for ${recordLabel} and ${pillarLabel}.`,
        prompt:
          `Review the Individual Outcomes page for ${recordLabel} and ${pillarLabel} and explain the relationship between the selected employee record and pillar score. Highlight what the scatter plot or table reveals about individual performance, and whether any patterns are concerning or promising.`,
      },
      {
        title: 'Individual Risk Signals',
        displayText: `Identify any risk signals for ${recordLabel} and ${pillarLabel}.`,
        prompt:
          `Identify risk signals in the individual outcomes data shown for ${recordLabel} and ${pillarLabel}. Explain which individuals or records appear to be outliers, whether the selected pillar is weak or strong, and what those signals mean for targeted support.`,
      },
      {
        title: 'Individual Action Recommendations',
        displayText: `Recommend follow-up actions for ${recordLabel} and ${pillarLabel}.`,
        prompt:
          `Based on the individual outcomes for ${recordLabel} and ${pillarLabel}, recommend follow-up actions leaders should take. Focus on how to support individuals or better align record-based performance with pillar strength.`,
      },
    ]);

    setPageInfoTitle('About Individual Outcomes');
    setPageInfoIntro(
      'This page connects individual employee records with pillar-level outcomes, helping leaders understand how personal metrics align with culture and performance signals.',
    );
    setPageInfoItems([
      {
        term: 'Individual correlation',
        description: 'See how individual records relate to pillar scores and outcomes.',
      },
      {
        term: 'Outlier signals',
        description: 'Spot extraordinary individual patterns that may need attention.',
      },
      {
        term: 'Targeted actions',
        description: 'Use these insights to support individuals and improve team outcomes.',
      },
    ]);

    return () => {
      setPagePrompts([]);
      setPageInfoItems([]);
      setPageInfoTitle(undefined);
      setPageInfoIntro(undefined);
    };
  }, [setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro, selectedRecordName, selectedPillarName]);

  useEffect(() => {
    if (data_by_option && data_by_option.length > 0) {
      const pillars: SelectOptions[] = Array.from(
        new Map<string, SelectOptions>(
          data_by_option[0]?.result.pillars.map(
            (score: { pillar_name: string; pillar: number }) => [
              score.pillar,
              { value: score.pillar, label: score.pillar_name },
            ]
          )
        ).values()
      );
      setFilterPillars(pillars);

      // Only set initial value if not already set, or if current selection is not in new list
      if (selectedPillar === 0 || !pillars.some(p => p.value === selectedPillar)) {
        setSelectedPillar(pillars[0].value);
        setSelectedPillarName(pillars[0].label);
      }
    }
  }, [data_by_option]);

  useEffect(() => {
    if (lookups && lookups.length > 0) {
      const lookupData = lookups[0]?.result;
      const records: SelectOptions[] = Array.from(
        new Map<string, SelectOptions>(
          lookupData.employee_record_lookup.map((record: { name: string; id: number }) => [
            record.id,
            { value: record.id, label: record.name },
          ])
        ).values()
      ).sort((a, b) => a.label.localeCompare(b.label));
      setFilterRecords(records);

      // Only set initial value if not already set, or if current selection is not in new list
      if (records.length > 0 && (selectedRecord === 0 || !records.some(r => r.value === selectedRecord))) {
        setSelectedRecord(records[0].value);
        setSelectedRecordName(records[0].label);
      }
    }
  }, [lookups]);

  useEffect(() => {
    if (selectedPillar !== undefined && employee_records && employee_records.length > 0 && employee_record_values) {
      const recordData = employee_records[0]?.result;

      // Create a map of employee ID to record value
      const recordValuesByEmployee = new Map();
      employee_record_values.forEach((record: any) => {
        // New API format has employee_id and value directly, and record_id is null for employees without data
        if (record.employee_id && record.value !== null && record.record_id !== null) {
          const value = parseFloat(record.value);
          if (!isNaN(value)) {
            recordValuesByEmployee.set(record.employee_id, value);
          }
        }
      });

      const formattedData = recordData
        .map((item: { employee_id: number; pillars: any[] }) => {
          const pillarScore = item.pillars
            .filter(pillar => pillar.factor === selectedPillar)
            .map(pillar => parseFloat(pillar.average_response.toFixed(1)))[0];

          const recordValue = recordValuesByEmployee.get(item.employee_id);
          return { x: pillarScore, y: recordValue };
        })
        .filter(
          (point: { x: undefined; y: undefined }) => point.x !== undefined && point.y !== undefined
        ); // Filter out invalid points

      // Calculate the trend line
      const calculateTrendLine = (data: any[]) => {
        const n = data.length;
        const sumX = data.reduce((sum, point) => sum + point.x, 0);
        const sumY = data.reduce((sum, point) => sum + point.y, 0);
        const sumXY = data.reduce((sum, point) => sum + point.x * point.y, 0);
        const sumX2 = data.reduce((sum, point) => sum + point.x ** 2, 0);

        const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX ** 2);
        const intercept = (sumY - slope * sumX) / n;

        // Extrapolate trend line from x=0 to x=10
        return [
          { x: 0, y: slope * 0 + intercept },
          { x: 10, y: slope * 10 + intercept },
        ];
      };

      const trendLine = calculateTrendLine(formattedData);
      setChartData(formattedData);
      setChartTrendLine(trendLine);
    }
  }, [employee_records, employee_record_values, selectedPillar, selectedRecord]);

  const data = {
    datasets: [
      {
        data: chartData ?? [],
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        pointRadius: 5,
        showLine: false, // Explicitly set for scatter plots
      },
      {
        label: 'Trend Line',
        data: chartTrendLine ?? [],
        borderColor: 'rgba(75, 75, 192, 1)',
        borderWidth: 2,
        showLine: true,
        tension: 0.3,
        fill: false,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text:
            selectedPillar !== undefined && filterPillars[selectedPillar]
              ? filterPillars[selectedPillar]?.label
              : '',
        },
        min: 0,
        max: 10,
      },
      y: {
        title: {
          display: true,
          text:
            selectedRecord !== undefined &&
              filterRecords.find(item => item.value === selectedRecord)?.label
              ? filterRecords.find(item => item.value === selectedRecord)?.label
              : '',
        },
      },
    },
  };

  return (
    <PageContent>
      <PageSection padbottom="10px">
        <PageHeader
          title="Individual Outcomes"
          caption="Individual outcomes coorelated with individual pillar scores"
        />
      </PageSection>

      <PageSection
        padright
        display="flex"
        gap="10px"
        alignItems="flex-start"
        style={{ minWidth: '400px', maxHeight: 'calc(100vh - 150px)' }}
      >
        <StyledCard style={{ maxHeight: '100%', overflow: 'auto' }}>
          <PageSection padtop padright="15px" padleft padbottom>
            <>
              <PageSection padbottom display="flex" justifyContent="space-between">
                <Subtitle>{`${selectedRecordName} and ${selectedPillarName}`}</Subtitle>
                <ControlContainer justify="right">
                  <Select
                    options={filterRecords}
                    placeholder="Select Record"
                    value={filterRecords.find(option => option.value === selectedRecord)}
                    onChange={option => {
                      setSelectedRecord(option ? option.value : 0);
                      setSelectedRecordName(option ? option.label : null);
                    }}
                    styles={{
                      container: base => ({
                        ...base,
                        width: '250px',
                      }),
                    }}
                  />
                  <Select
                    options={filterPillars}
                    placeholder="Select Pillar"
                    value={filterPillars.find(option => option.value === selectedPillar)}
                    onChange={option => {
                      setSelectedPillar(option ? option.value : 0);
                      setSelectedPillarName(option ? option.label : null);
                    }}
                    styles={{
                      container: base => ({
                        ...base,
                        width: '250px',
                      }),
                    }}
                  />
                  <StyledIconButton
                    icon={showDataTable ? 'chart line' : 'edit outline'}
                    title={showDataTable ? 'Show Chart' : 'Show Data Table'}
                    onClick={() => setShowDataTable(!showDataTable)}
                    disabled={false}
                  />
                </ControlContainer>
              </PageSection>
              {showDataTable ? (
                <OutcomesIndividualDataTable
                  employee_record_values={employee_record_values}
                  selectedRecordName={selectedRecordName}
                  onAddRecord={handleAddRecord}
                  onEditRecord={handleEditRecord}
                  onDeleteRecord={handleDeleteClick}
                />
              ) : (
                <PageSection display="flex" justifyContent="center" style={{ height: '400px', maxHeight: '60vh' }}>
                  <Scatter data={data} options={options} />
                </PageSection>
              )}
            </>
          </PageSection>
        </StyledCard>
      </PageSection>

      <IndividualRecordDataModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        recordValueId={selectedRecord}
        recordValueName={selectedRecordName || ''}
        assessmentPeriodId={currentAssessmentPeriodId}
        employeeId={selectedEmployeeId}
        employeeName={selectedEmployeeName}
        existingRecord={editingRecord}
      />

      <Confirm
        open={showDeleteConfirm}
        header="Delete Employee Record"
        content="Are you sure you want to delete this employee record?"
        onCancel={() => {
          setShowDeleteConfirm(false);
          setRecordToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
      />
    </PageContent>
  );
};

export default OutcomesIndividual;

